import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useCmsPanel } from '@/contexts/CmsPanelContext';

/**
 * Reusable push-panel for creating/editing an entity.
 * Renders into the CmsLayout right panel container via portal.
 */
export default function EntityFormDialog({
  open,
  onOpenChange,
  title,
  trigger,
  onSubmit,
  submitLabel = 'Зачувај',
  children,
  maxWidth,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  trigger?: React.ReactNode;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel?: string;
  children: React.ReactNode;
  maxWidth?: string;
}) {
  const { openPanel, closePanel, panelContainer } = useCmsPanel();

  useEffect(() => {
    if (open) openPanel();
    else closePanel();
  }, [open, openPanel, closePanel]);

  return (
    <>
      {trigger}
      {open && panelContainer && createPortal(
        <div className="relative p-6 h-full flex flex-col">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          <div className="flex flex-col space-y-2 text-left mb-4">
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          </div>
          <form onSubmit={onSubmit} className="space-y-4 flex-1 overflow-y-auto">
            {children}
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Откажи
              </Button>
              <Button type="submit">{submitLabel}</Button>
            </div>
          </form>
        </div>,
        panelContainer
      )}
    </>
  );
}
